import type { SxProps, Theme } from '@mui/material/styles';
import {
  adminBorders,
  adminColors,
  adminControlHeights,
  adminLayout,
  adminRadii,
  adminShadows,
  adminSpacing,
  adminTypography,
} from './adminDesignTokens';

export type AdminSx = SxProps<Theme>;

export type AdminButtonTone = 'primary' | 'secondary' | 'danger';
export type AdminStatusPillTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';
export type AdminAlertTone = 'info' | 'success' | 'warning' | 'danger';

export const adminCombineSx = (...items: Array<AdminSx | false | null | undefined>): AdminSx =>
  items.flatMap((item) => {
    if (!item) return [];
    return Array.isArray(item) ? item : [item];
  }) as AdminSx;

export const adminSurfaceSx = {
  backgroundColor: adminColors.surface,
  border: adminBorders.surface,
  borderRadius: adminRadii.md,
  boxShadow: adminShadows.none,
} as const satisfies AdminSx;

export const adminAppShellSx = {
  backgroundColor: adminColors.appBackground,
  color: adminColors.text,
  display: 'flex',
  minHeight: '100vh',
  height: '100%',
} as const satisfies AdminSx;

export const adminMainContentSx = {
  flexGrow: 1,
  minHeight: '100vh',
} as const satisfies AdminSx;

export const adminMainContentInnerSx = {
  p: { xs: adminSpacing.md, md: adminSpacing.lg },
} as const satisfies AdminSx;

export const adminSidebarPaperSx = {
  backgroundColor: adminColors.surface,
  borderRight: 'none',
  boxShadow: '0px 3px 16px rgb(0 0 0 / 8%)',
  boxSizing: 'border-box',
} as const satisfies AdminSx;

export const adminSidebarHeaderSx = {
  backgroundColor: adminColors.surface,
  borderBottom: adminBorders.divider,
  px: adminSpacing.lg,
  py: adminSpacing.md,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const satisfies AdminSx;

export const adminSidebarSectionLabelSx = {
  ...adminTypography.helper,
  color: 'rgb(34 43 69 / 48%)',
  display: 'block',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  px: adminSpacing.md,
  py: adminSpacing.xs,
  textTransform: 'uppercase',
} as const satisfies AdminSx;

export const adminSidebarNavItemSx = {
  borderRadius: adminRadii.sm,
  color: adminColors.text,
  minHeight: 34,
  mx: adminSpacing.sm,
  mb: 0.25,
  px: adminSpacing.sm,
  py: 0.625,
  '& .MuiListItemIcon-root': {
    color: adminColors.mutedText,
    minWidth: 32,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '& .MuiListItemText-primary': {
    ...adminTypography.body,
    color: 'inherit',
  },
  '&:hover': {
    backgroundColor: adminColors.primarySoft,
  },
  '&.Mui-selected': {
    backgroundColor: adminColors.primary,
    color: adminColors.surface,
    '&:hover': {
      backgroundColor: adminColors.primaryHover,
    },
    '& .MuiListItemIcon-root': {
      color: adminColors.surface,
    },
    '& .MuiListItemText-primary': {
      fontWeight: adminTypography.bodyStrong.fontWeight,
    },
  },
} as const satisfies AdminSx;

export const adminSidebarUserActionSx = {
  alignItems: 'center',
  borderRadius: adminRadii.sm,
  cursor: 'pointer',
  display: 'flex',
  gap: adminSpacing.sm,
  mx: adminSpacing.xs,
  p: adminSpacing.sm,
  '&:hover': {
    backgroundColor: adminColors.dangerBackground,
  },
} as const satisfies AdminSx;

export const adminSidebarAvatarSx = {
  backgroundColor: adminColors.text,
  borderRadius: adminRadii.sm,
  color: adminColors.surface,
  fontSize: adminTypography.bodyStrong.fontSize,
  fontWeight: adminTypography.bodyStrong.fontWeight,
  height: 32,
  width: 32,
} as const satisfies AdminSx;

export const adminPageHeaderSx = {
  alignItems: { xs: 'flex-start', md: 'center' },
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: adminSpacing.md,
  justifyContent: 'space-between',
  pb: adminSpacing.lg,
  '& .AdminPageHeader-title': {
    ...adminTypography.pageTitle,
    color: adminColors.text,
    letterSpacing: 0,
  },
  '& .AdminPageHeader-meta': {
    ...adminTypography.pageMeta,
    color: adminColors.mutedText,
  },
} as const satisfies AdminSx;

export const adminCardHeaderSx = {
  alignItems: 'center',
  display: 'flex',
  gap: adminSpacing.sm,
  justifyContent: 'space-between',
  mb: adminSpacing.sm,
  '& .AdminCardHeader-title': {
    ...adminTypography.cardTitle,
    color: adminColors.text,
  },
  '& .AdminCardHeader-meta': {
    ...adminTypography.helper,
    color: adminColors.subtleText,
  },
} as const satisfies AdminSx;

export const adminCardContentSx = {
  p: adminSpacing.lg,
  '&:last-child': {
    pb: adminSpacing.lg,
  },
} as const satisfies AdminSx;

export const adminIconTitleSx = {
  alignItems: 'center',
  display: 'flex',
  gap: adminSpacing.sm,
  '& .AdminIconTitle-icon': {
    alignItems: 'center',
    color: adminColors.primary,
    display: 'flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  '& .AdminIconTitle-title': {
    ...adminTypography.cardTitle,
    color: adminColors.text,
  },
} as const satisfies AdminSx;

export const adminDividerSx = {
  borderColor: adminColors.divider,
} as const satisfies AdminSx;

export const adminDialogSx = {
  backgroundColor: adminColors.surface,
  border: adminBorders.surface,
  borderRadius: adminRadii.md,
  boxShadow: adminShadows.dialog,
  maxHeight: adminLayout.dialogMaxHeight,
} as const satisfies AdminSx;

export const adminDialogTitleSx = {
  backgroundColor: adminColors.surface,
  borderBottom: adminBorders.divider,
  color: adminColors.text,
  fontSize: '16px',
  fontWeight: 700,
  px: adminSpacing.lg,
  py: 1.25,
} as const satisfies AdminSx;

export const adminDialogContentSx = {
  p: adminSpacing.md,
} as const satisfies AdminSx;

export const adminDialogActionsSx = {
  borderTop: adminBorders.divider,
  gap: adminSpacing.sm,
  px: adminSpacing.lg,
  py: 1.25,
} as const satisfies AdminSx;

export const adminButtonSx = {
  borderRadius: adminRadii.sm,
  fontSize: adminTypography.button.fontSize,
  fontWeight: adminTypography.button.fontWeight,
  lineHeight: adminTypography.button.lineHeight,
  minHeight: adminControlHeights.buttonSmall,
  px: 1.5,
  textTransform: 'none',
  '&.Mui-disabled': {
    backgroundColor: adminColors.neutralBackground,
    borderColor: adminColors.border,
    color: adminColors.disabledText,
  },
} as const satisfies AdminSx;

export const adminButtonToneSx: Record<AdminButtonTone, AdminSx> = {
  primary: {
    backgroundColor: adminColors.primary,
    borderColor: adminColors.primary,
    color: adminColors.surface,
    '&:hover': {
      backgroundColor: adminColors.primaryHover,
      borderColor: adminColors.primaryHover,
    },
  },
  secondary: {
    backgroundColor: adminColors.surface,
    borderColor: adminColors.primary,
    color: adminColors.primary,
    '&:hover': {
      backgroundColor: adminColors.primarySoft,
      borderColor: adminColors.primary,
    },
  },
  danger: {
    backgroundColor: adminColors.danger,
    borderColor: adminColors.danger,
    color: adminColors.surface,
    '&:hover': {
      backgroundColor: adminColors.dangerHover,
      borderColor: adminColors.dangerHover,
    },
  },
};

export const adminLinkButtonSx = {
  color: adminColors.primary,
  cursor: 'pointer',
  fontSize: adminTypography.body.fontSize,
  fontWeight: 500,
  minWidth: 0,
  p: 0,
  textDecoration: 'underline',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: adminColors.primaryHover,
    textDecoration: 'underline',
  },
  '&.Mui-disabled': {
    color: adminColors.disabledText,
  },
} as const satisfies AdminSx;

export const adminTextLinkSx = {
  color: adminColors.primary,
  cursor: 'pointer',
  fontSize: adminTypography.body.fontSize,
  fontWeight: 500,
  textDecoration: 'underline',
  '&:hover': {
    color: adminColors.primaryHover,
  },
} as const satisfies AdminSx;

export const adminIconActionButtonSx = {
  borderRadius: adminRadii.sm,
  color: adminColors.primary,
  height: 24,
  p: 0.5,
  width: 24,
  '&:hover': {
    backgroundColor: adminColors.primarySoft,
  },
  '&.Mui-disabled': {
    color: adminColors.disabledText,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
} as const satisfies AdminSx;

export const adminFormFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: adminColors.fieldBackground,
    borderRadius: adminRadii.sm,
    color: adminColors.text,
    fontSize: adminTypography.body.fontSize,
    minHeight: adminControlHeights.fieldSmall,
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderColor: adminColors.border,
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: adminColors.borderHover,
    },
    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: adminColors.primary,
      boxShadow: 'none',
    },
    '&.Mui-error fieldset, &.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: adminColors.danger,
    },
    '&.Mui-error.Mui-focused fieldset, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: adminColors.danger,
      boxShadow: '0 0 0 2px rgba(180, 35, 24, 0.12)',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: adminTypography.body.fontSize,
    px: 1.25,
    py: '7px',
  },
  '& .MuiSelect-select': {
    px: '10px !important',
    py: '7px !important',
  },
  '& .MuiInputLabel-root': {
    color: adminColors.mutedText,
    fontSize: adminTypography.body.fontSize,
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: adminColors.danger,
  },
  '& .MuiFormHelperText-root': {
    ...adminTypography.helper,
    mx: 0,
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: adminColors.danger,
  },
} as const satisfies AdminSx;

export const adminFormControlSx = {
  ...adminFormFieldSx,
} as const satisfies AdminSx;

export const adminFormRowSx = {
  alignItems: 'flex-start',
  display: 'grid',
  gap: { xs: adminSpacing.xs, sm: adminSpacing.sm },
  gridTemplateColumns: {
    xs: '1fr',
    sm: `${adminLayout.formLabelColumn} minmax(0, 1fr)`,
  },
  '& .AdminFormRow-label': {
    ...adminTypography.label,
    color: adminColors.mutedText,
    pt: { xs: 0, sm: '8px' },
  },
  '& .AdminFormRow-required': {
    color: adminColors.danger,
  },
  '& .AdminFormRow-field': {
    minWidth: 0,
  },
} as const satisfies AdminSx;

export const adminFieldLabelSx = {
  ...adminTypography.label,
  color: '#222b4599',
} as const satisfies AdminSx;

export const adminInlineControlLabelSx = {
  gap: '4px',
  margin: '0 !important',
  '& .MuiCheckbox-root': {
    margin: 0,
    padding: 0,
  },
  '& .MuiFormControlLabel-label': {
    ...adminTypography.helper,
    color: adminColors.mutedText,
    fontWeight: 500,
    paddingLeft: 0,
  },
} as const satisfies AdminSx;

export const adminCheckboxSx = {
  m: 0,
  p: 0,
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '&.Mui-checked': {
    color: adminColors.primary,
  },
} as const satisfies AdminSx;

export const adminSectionHeadingSx = {
  color: adminColors.primary,
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  lineHeight: 1.3,
  textTransform: 'uppercase',
} as const satisfies AdminSx;

export const adminFormErrorTextSx = {
  ...adminTypography.helper,
  alignItems: 'center',
  color: adminColors.danger,
  display: 'inline-flex',
  gap: adminSpacing.xs,
  mt: adminSpacing.xs,
} as const satisfies AdminSx;

export const adminReadOnlyFieldSx = {
  ...adminTypography.bodyStrong,
  alignItems: 'center',
  color: adminColors.text,
  display: 'flex',
  minHeight: adminControlHeights.fieldSmall,
  overflowWrap: 'anywhere',
  py: '7px',
} as const satisfies AdminSx;

export const adminTableSx = {
  '& .MuiTableHead-root .MuiTableRow-root': {
    backgroundColor: adminColors.tableHead,
  },
  '& .MuiTableCell-head': {
    ...adminTypography.tableHeader,
    color: adminColors.text,
    p: '6px 8px',
  },
  '& .MuiTableSortLabel-root': {
    color: `${adminColors.text} !important`,
    fontSize: adminTypography.tableHeader.fontSize,
    fontWeight: adminTypography.tableHeader.fontWeight,
    justifyContent: 'inherit',
  },
  '& .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
    ml: 0.5,
    mr: 0,
  },
  '& .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active': {
    color: `${adminColors.primary} !important`,
  },
  '& .MuiTableSortLabel-icon': {
    color: `${adminColors.disabledText} !important`,
    opacity: 1,
  },
  '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
    color: `${adminColors.primary} !important`,
  },
  '& .MuiTableCell-alignCenter .MuiTableSortLabel-root': {
    justifyContent: 'center',
    mx: 'auto',
    position: 'relative',
    width: 'fit-content',
  },
  '& .MuiTableCell-alignCenter .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
    ml: 0,
    position: 'absolute',
    right: -20,
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    height: adminControlHeights.tableRowDense,
    '&:nth-of-type(even)': {
      backgroundColor: adminColors.surfaceAlt,
    },
    '&:hover': {
      backgroundColor: adminColors.infoBackground,
    },
  },
  '& .MuiTableCell-body': {
    ...adminTypography.body,
    borderBottom: adminBorders.divider,
    color: adminColors.text,
    p: '4px 8px',
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: adminControlHeights.tablePagination,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: adminTypography.helper.fontSize,
  },
} as const satisfies AdminSx;

export const adminListViewSx = {
  ...adminSurfaceSx,
  overflow: 'hidden',
  '& .MuiListItem-root': {
    borderBottom: adminBorders.divider,
    minHeight: 36,
    px: adminSpacing.md,
    py: adminSpacing.xs,
  },
  '& .MuiListItem-root:last-of-type': {
    borderBottom: 0,
  },
  '& .MuiListItemText-primary': {
    ...adminTypography.bodyStrong,
    color: adminColors.text,
  },
  '& .MuiListItemText-secondary': {
    ...adminTypography.helper,
    color: adminColors.mutedText,
  },
} as const satisfies AdminSx;

export const adminTabsSx = {
  minHeight: 36,
  '& .MuiTabs-flexContainer': {
    gap: adminSpacing.lg,
  },
  '& .MuiTabs-indicator': {
    backgroundColor: adminColors.primary,
    height: 3,
  },
  '& .MuiTab-root': {
    color: adminColors.text,
    fontSize: adminTypography.bodyStrong.fontSize,
    fontWeight: 500,
    minHeight: 36,
    minWidth: 'auto',
    px: 0,
    py: 0.75,
    textTransform: 'none',
  },
  '& .MuiTab-root.Mui-selected': {
    color: adminColors.primary,
    fontWeight: 600,
  },
} as const satisfies AdminSx;

export const adminStatusPillSx = {
  alignItems: 'center',
  borderRadius: adminRadii.sm,
  display: 'inline-flex',
  fontSize: adminTypography.pill.fontSize,
  fontWeight: adminTypography.pill.fontWeight,
  gap: adminSpacing.xs,
  justifyContent: 'center',
  lineHeight: adminTypography.pill.lineHeight,
  minHeight: '16px',
  px: adminSpacing.sm,
  py: 0.25,
  whiteSpace: 'nowrap',
  width: 'fit-content',
} as const satisfies AdminSx;

export const adminStatusPillToneSx: Record<AdminStatusPillTone, AdminSx> = {
  success: {
    backgroundColor: adminColors.successBackground,
    border: `1px solid ${adminColors.successBackground}`,
    color: adminColors.success,
  },
  warning: {
    backgroundColor: adminColors.warningBackground,
    border: `1px solid ${adminColors.warningBackground}`,
    color: adminColors.warning,
  },
  danger: {
    backgroundColor: adminColors.dangerBackground,
    border: `1px solid ${adminColors.dangerBackground}`,
    color: adminColors.danger,
  },
  neutral: {
    backgroundColor: adminColors.neutralBackground,
    border: `1px solid ${adminColors.neutralBackground}`,
    color: adminColors.neutral,
  },
  info: {
    backgroundColor: adminColors.primarySoft,
    border: `1px solid ${adminColors.primarySoft}`,
    color: adminColors.primary,
  },
};

export const adminSearchFieldSx = {
  ...adminFormFieldSx,
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: adminColors.subtleText,
    fontSize: 18,
  },
} as const satisfies AdminSx;

export const adminAutocompleteSx = {
  ...adminFormFieldSx,
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'flex-start',
    minHeight: adminControlHeights.fieldSmall,
    py: '1px',
  },
  '& .MuiChip-root': {
    borderRadius: adminRadii.sm,
    fontSize: adminTypography.helper.fontSize,
    height: adminControlHeights.pill,
  },
  '& .MuiAutocomplete-option': {
    fontSize: adminTypography.body.fontSize,
    minHeight: 32,
  },
} as const satisfies AdminSx;

export const adminInfoPanelSx = {
  backgroundColor: adminColors.infoBackground,
  border: `1px solid ${adminColors.primarySoft}`,
  borderRadius: adminRadii.md,
  color: adminColors.text,
  p: adminSpacing.md,
} as const satisfies AdminSx;

export const adminUploadDropzoneSx = {
  ...adminSurfaceSx,
  backgroundColor: adminColors.surfaceAlt,
  border: adminBorders.dashed,
  minHeight: 96,
  overflow: 'hidden',
  position: 'relative',
} as const satisfies AdminSx;

export const adminUploadDropzoneContentSx = {
  alignItems: 'center',
  color: adminColors.primary,
  display: 'flex',
  flexDirection: 'column',
  gap: adminSpacing.xs,
  height: '100%',
  justifyContent: 'center',
  minHeight: 96,
  px: adminSpacing.md,
  textAlign: 'center',
  '& .AdminUploadDropzone-label': {
    ...adminTypography.bodyStrong,
    color: adminColors.primary,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  '& .AdminUploadDropzone-helper': {
    ...adminTypography.helper,
    color: adminColors.mutedText,
  },
} as const satisfies AdminSx;

export const adminFileInputOverlaySx = {
  cursor: 'pointer',
  display: 'block',
  height: '100%',
  left: 0,
  opacity: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
  '&:disabled': {
    cursor: 'not-allowed',
  },
} as const satisfies AdminSx;

export const adminBottomBannerSx = {
  backgroundColor: adminColors.warningBackground,
  borderTop: adminBorders.divider,
  borderRadius: `0 0 ${adminRadii.md} ${adminRadii.md}`,
  color: adminColors.primary,
  px: adminSpacing.md,
  py: adminSpacing.xs,
  textAlign: 'center',
  '& .AdminBottomBanner-text': {
    ...adminTypography.helper,
    color: '#222b45',
    display: 'block', 
    fontWeight: 500,
  },
} as const satisfies AdminSx;

export const adminLogPanelSx = {
  ...adminSurfaceSx,
  alignItems: 'center',
  backgroundColor: adminColors.surfaceAlt,
  display: 'flex',
  gap: adminSpacing.md,
  justifyContent: 'space-between',
  minHeight: 56,
  px: adminSpacing.lg,
  py: adminSpacing.md,
} as const satisfies AdminSx;

export const adminAlertSx = {
  alignItems: 'flex-start',
  borderRadius: adminRadii.md,
  fontSize: adminTypography.body.fontSize,
  px: adminSpacing.md,
  py: adminSpacing.sm,
  '& .MuiAlert-icon': {
    py: 0.25,
  },
  '& .MuiAlert-message': {
    ...adminTypography.body,
    py: 0.25,
  },
} as const satisfies AdminSx;

export const adminAlertToneSx: Record<AdminAlertTone, AdminSx> = {
  info: {
    backgroundColor: adminColors.infoBackground,
    border: `1px solid ${adminColors.primarySoft}`,
    color: adminColors.text,
  },
  success: {
    backgroundColor: adminColors.successBackground,
    border: `1px solid ${adminColors.successBackground}`,
    color: adminColors.text,
  },
  warning: {
    backgroundColor: adminColors.warningBackground,
    border: `1px solid ${adminColors.warningBackground}`,
    color: adminColors.text,
  },
  danger: {
    backgroundColor: adminColors.dangerBackground,
    border: `1px solid ${adminColors.dangerBackground}`,
    color: adminColors.text,
  },
};

export const adminEmptyStateSx = {
  ...adminSurfaceSx,
  alignItems: 'center',
  border: adminBorders.dashed,
  color: adminColors.mutedText,
  display: 'flex',
  flexDirection: 'column',
  gap: adminSpacing.sm,
  justifyContent: 'center',
  minHeight: 120,
  px: adminSpacing.lg,
  py: adminSpacing.xl,
  textAlign: 'center',
  '& .AdminEmptyState-title': {
    ...adminTypography.bodyStrong,
    color: adminColors.text,
  },
  '& .AdminEmptyState-copy': {
    ...adminTypography.helper,
    color: adminColors.mutedText,
  },
} as const satisfies AdminSx;
